import React, { forwardRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { space, width } from 'styled-system';
import { Spinner as BPSpinner } from '@blueprintjs/core';
import { SpinnerProps } from 'core/components/types';
import position from './utils/position';

type CSSCustomVar = React.CSSProperties & { '--d'?: string };

const BaseSpinner = styled(BPSpinner)<Omit<SpinnerProps, 'variant'>>`
  ${space};
  ${width};
  ${position};
`;

// A single grain pulses bright once per cycle. Offsetting the delay down each
// column makes the bright grain appear to fall (left tube) / rise (right tube).
const grainflow = keyframes`
  0% { opacity: 0.14; }
  9% { opacity: 1; }
  30% { opacity: 0.14; }
  100% { opacity: 0.14; }
`;

const HOURGLASS_DURATION = 0.9; // seconds, full cycle
const DOTS_PER_COLUMN = 4; // fewer, chunkier grains read better at small sizes
const DOT_SIZE = 2; // each grain is DOT_SIZE x DOT_SIZE units
const DOT_PITCH = 3; // distance between successive dot origins in a column
const COLUMN_GAP = 3; // gap between the two tubes
const VB_WIDTH = DOT_SIZE + COLUMN_GAP + DOT_SIZE; // viewBox width
const VB_HEIGHT = (DOTS_PER_COLUMN - 1) * DOT_PITCH + DOT_SIZE; // viewBox height
const RIGHT_COLUMN_X = DOT_SIZE + COLUMN_GAP; // x origin of right tube

const HourglassSvg = styled.svg<Omit<SpinnerProps, 'variant'>>`
  display: inline-block;
  color: ${({ theme, color }) => (color && theme.colors[color as keyof typeof theme.colors]) || 'currentColor'};
  ${space};
  ${width};
  ${position};

  rect {
    fill: currentColor;
    shape-rendering: crispEdges;
    animation: ${grainflow} ${HOURGLASS_DURATION}s linear infinite;
    animation-delay: var(--d, 0s);
  }

  @media (prefers-reduced-motion: reduce) {
    rect {
      animation: none;
      opacity: 0.55;
    }
  }
`;

const HourglassSpinner = forwardRef<SVGSVGElement, Omit<SpinnerProps, 'variant'>>(
  ({ size = 16, intent, ...rest }, ref) => {
    const step = HOURGLASS_DURATION / DOTS_PER_COLUMN;
    const dots = [];
    const numericSize = Number(size) || 16;

    for (let i = 0; i < DOTS_PER_COLUMN; i += 1) {
      const y = i * DOT_PITCH;
      const leftDelay = (i * step).toFixed(3);
      const rightDelay = ((DOTS_PER_COLUMN - 1 - i) * step).toFixed(3);

      // left tube: grain falls (delay grows top -> bottom)
      dots.push(
        <rect
          key={`l${i}`}
          x={0}
          y={y}
          width={DOT_SIZE}
          height={DOT_SIZE}
          style={{ '--d': `${leftDelay}s` } as CSSCustomVar}
        />
      );
      // right tube: grain rises (delays reversed)
      dots.push(
        <rect
          key={`r${i}`}
          x={RIGHT_COLUMN_X}
          y={y}
          width={DOT_SIZE}
          height={DOT_SIZE}
          style={{ '--d': `${rightDelay}s` } as CSSCustomVar}
        />
      );
    }

    return (
      <HourglassSvg
        ref={ref}
        // Bind to the precise type mapped by styled-components/styled-system to fix the intersection error
        width={((numericSize * VB_WIDTH) / VB_HEIGHT) as React.ComponentProps<typeof HourglassSvg>['width']}
        height={numericSize as React.ComponentProps<typeof HourglassSvg>['height']}
        viewBox={`0 0 ${VB_WIDTH} ${VB_HEIGHT}`}
        xmlns="http://www.w3.org/2000/svg"
        role="status"
        aria-label="Loading"
        intent={intent}
        {...rest}
      >
        {dots}
      </HourglassSvg>
    );
  }
);

// The generic ref expects EITHER a BlueprintJS Spinner instance or our SVG element
const Spinner = forwardRef<BPSpinner | SVGSVGElement, SpinnerProps>(({ variant, ...props }, ref) => {
  if (variant === 'hourglass') {
    return <HourglassSpinner ref={ref as React.Ref<SVGSVGElement>} {...props} />;
  }

  return <BaseSpinner ref={ref as React.Ref<BPSpinner>} {...props} />;
});

export default Spinner;
